import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Rounds a date to the nearest specified minute interval
 * Useful for: Time tracking, scheduling systems, meeting scheduling
 */
export const getRoundedDate = (minutes: number, d = new Date()) => {
  let ms = 1000 * 60 * minutes; // convert minutes to ms
  let roundedDate = new Date(Math.round(d.getTime() / ms) * ms);
  return roundedDate;
};

/**
 * Formats a date for HTML date input fields
 * Useful for: Any form with date inputs, task creation, order forms
 */
export const formatDateForForm = (date: Date | string | undefined, defaultDate: Date) => {
  return dayjs(date || defaultDate).format("YYYY-MM-DD");
};

/**
 * Formats a time for HTML time input fields
 * Useful for: Any form with time inputs, scheduling forms, time tracking
 */
export const formatTimeForForm = (date: Date | string | undefined, defaultDate: Date) => {
  return dayjs(date || defaultDate).format("HH:mm");
};

/**
 * Creates a UTC date from date and time strings
 * Useful for: API calls, database operations, international applications
 */
export const createUTCDate = (dateString: string, timeString: string) => {
  return dayjs.utc(dateString + " " + timeString).toDate();
};

/**
 * Formats a date for display with optional time
 * Useful for: Event displays, task lists, order summaries
 */
export const formatDisplayDate = (date: Date, showTime: boolean = false) => {
  return dayjs(date).format(showTime ? 'dddd DD MMMM YYYY HH:mm' : 'dddd DD MMMM YYYY');
};

/**
 * Gets a relative time string (e.g., "2 hours ago", "in 3 days")
 * Useful for: Notifications, activity feeds, time-sensitive displays
 */
export const getRelativeTime = (date: Date) => {
  return dayjs(date).fromNow();
};

/**
 * Checks if a date is today
 * Useful for: Highlighting current day, conditional styling
 */
export const isToday = (date: Date) => {
  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * Checks if a date is in the future
 * Useful for: Upcoming events, due dates, scheduling validation
 */
export const isFuture = (date: Date) => {
  return dayjs(date).isAfter(dayjs());
};

/**
 * Gets the start of a week for a given date
 * Useful for: Week-based views, reporting periods
 */
export const getWeekStart = (date: Date = new Date()) => {
  return dayjs(date).startOf('week').toDate();
};

/**
 * Gets the end of a week for a given date
 * Useful for: Week-based views, reporting periods
 */
export const getWeekEnd = (date: Date = new Date()) => {
  return dayjs(date).endOf('week').toDate();
};
